import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';

/**
 * PaywallGate — soft-paywall wrapper for public mirror routes (/docs/*, /guides/*).
 *
 * **Crawler view:** the full `children` content is in the HTML. Google indexes it.
 * **Logged-in user view:** sees the full `children`.
 * **Logged-out user view:** sees only the first ~30% (fade-out) + a sign-up CTA.
 *
 * Google Flexible Sampling: we mark the page as paywalled in the parent Seo
 * component via Article JSON-LD (`isAccessibleForFree: false` + `hasPart`).
 * That tells Google "this is intentionally paywalled" so the soft gate isn't
 * treated as cloaking.
 *
 * Auth state is read from localStorage authToken — same source the rest of the
 * app uses. We render the full tree on first mount (so static HTML matches and
 * the SPA hydrates without a flash), then hide the tail of the content if the
 * user is logged out.
 */
export default function PaywallGate({ children, kind = 'article' }) {
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true); // optimistic — match HTML on first paint

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
    setAuthChecked(true);
  }, []);

  if (!authChecked || isLoggedIn) {
    return <>{children}</>;
  }

  const label = kind === 'guide' ? 'this guide' : 'this article';
  const ctaLabel = kind === 'guide' ? 'Read every guide free for 14 days' : 'Read every article free for 14 days';

  return (
    <div className="relative">
      {/* First ~30% of the content is visible. The fade is purely visual — the
          full content is still in the DOM for crawlers and accessibility tools. */}
      <div className="max-h-[420px] overflow-hidden relative">
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(10,11,15,0) 0%, rgba(10,11,15,0.9) 60%, rgba(10,11,15,1) 100%)' }}
          aria-hidden="true"
        />
      </div>

      {/* Sign-up CTA */}
      <div className="mt-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-dark-800 to-dark-900 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white">Keep reading {label} — free</h2>
            <p className="text-sm text-gray-400">Create a free haythix account to read the full content.</p>
          </div>
        </div>

        <ul className="space-y-1.5 text-sm text-gray-300 mb-5 mt-4">
          <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" /> 14-day free trial of every feature — no card required</li>
          <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" /> Full Help Center + Library access ({kind === 'guide' ? '102 worked-example guides' : '104 in-depth articles'})</li>
          <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" /> Multi-chain DEX + CEX trading bots, copy trading, AI signals</li>
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-lg shadow-amber-500/20"
          >
            Create free account <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-dark-600 hover:border-dark-500 text-gray-300 hover:text-white text-sm font-semibold transition-colors"
          >
            Sign in
          </Link>
          <span className="text-xs text-gray-500 ml-1">{ctaLabel}</span>
        </div>
      </div>
    </div>
  );
}
